import { useCallback, useEffect, useRef, useState } from "react";

import { Box } from "@mui/material";

import { LiveChatSection } from "components/LiveChatSection";
import { EVOTRUX_URL_HASH } from "constants/index";
import {
  useCarrierShipmentPageContext,
  useInterval,
  useScrollToHash,
  useUserChatInfo,
} from "hooks";
import {
  buildBookedShipmentChannelName,
  carrierHasBookingWriteAccess,
  channelExistsInChatInfo,
} from "utils";

interface Props {
  dividerComponent: JSX.Element;
}

const LIVE_CHAT_SECTION_ID = EVOTRUX_URL_HASH.LIVE_CHAT.slice(1);
const REFETCH_USER_CHANNELS_INTERVAL_MILLISECONDS = 500;

export function CarrierShipmentOverviewChat(props: Props) {
  const { dividerComponent } = props;
  const { shipment } = useCarrierShipmentPageContext();
  const { chatInfo, refreshChatInfo, refreshChatTokens } = useUserChatInfo();
  const [channelExists, setChannelExists] = useState<boolean>(false);
  const [tokensRefreshed, setTokensRefreshed] = useState<boolean>(false);

  const [channelName, setChannelName] = useState<string | null>(null);
  const shipmentRef = useRef<undefined | CondensedShipment>();

  useEffect(() => {
    /*
    This hook keeps track of the shipment ID between renders to ensure
    that the channel name is reset when another shipment is selected.
    */
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      setChannelName(buildBookedShipmentChannelName(shipment?.info.id || 0));
    }
  }, [shipment, setChannelName]);

  const refetchUserChatInfoOnChannelNotExisting = useCallback(() => {
    // nothing to do if no shipment or no user chat info available
    if (!shipment || !chatInfo || !channelName) {
      return;
    }

    // channel does not exist yet, poll
    if (!channelExists && !channelExistsInChatInfo(chatInfo, channelName)) {
      refreshChatInfo();
    }

    /*
      Channel exists now, update state variable and re-fetch tokens,
      such that user can make use of the chat
    */
    if (channelExistsInChatInfo(chatInfo, channelName) && !channelExists) {
      // chat was just created, need tokens that include the chat
      refreshChatTokens(() => {
        setTokensRefreshed(true);
      });
      setChannelExists(true);
    }
  }, [
    channelExists,
    chatInfo,
    channelName,
    refreshChatInfo,
    shipment,
    refreshChatTokens,
    setTokensRefreshed,
  ]);

  const isChatReady = channelExists && tokensRefreshed && channelName !== null;
  const sectionRef = useScrollToHash({
    hash: EVOTRUX_URL_HASH.LIVE_CHAT,
    isEnabled: isChatReady,
  });

  /*
    Poll until the chat conversation is created, then pause.
  */
  useInterval(
    refetchUserChatInfoOnChannelNotExisting,
    channelExists ? null : REFETCH_USER_CHANNELS_INTERVAL_MILLISECONDS,
  );

  // no shipment,
  // or shipment is not booked yet
  // or shipment not booked by carrier (or shared with carrier), return empty div
  if (
    shipment?.info.booked_at == null ||
    !carrierHasBookingWriteAccess(shipment)
  ) {
    return null;
  }

  return (
    <Box id={isChatReady ? LIVE_CHAT_SECTION_ID : undefined} ref={sectionRef}>
      {dividerComponent}
      <LiveChatSection channelName={channelName} isChatReady={isChatReady} />
    </Box>
  );
}
